import { useEffect, useState } from 'react';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';
import { Select } from '@tailwindComponents/Select';
import { useAuthHttp } from '@hooks';
import {
  FULFILLABLE_WAREHOUSES_URL,
  CONVERT_SALES_ORDER_URL,
  IGNORE_SALES_ORDER_URL,
} from '@constants';
import { OutboundProcess } from '@features/supplyChain/types';
import { FulfillableWarehousesResponse } from './types';

interface AssignWarehouseModalProps {
  draft: OutboundProcess | null;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onActioned: () => void;
}

type WarehouseOption = {
  label: string;
  value: string;
  disabled?: boolean;
  hint?: string;
};

type DeliveryAddress = NonNullable<
  NonNullable<OutboundProcess['salesOrder']>['deliveryAddress']
>;

const joinAddressLines = (address: DeliveryAddress): string =>
  [address.line1, address.line2, address.city].filter(Boolean).join(', ');

function CustomerDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className='text-xs text-gray-500'>{label}</dt>
      <dd className='text-sm text-gray-900'>{value || '—'}</dd>
    </div>
  );
}

export function AssignWarehouseModal({
  draft,
  isOpen,
  setIsOpen,
  onActioned,
}: AssignWarehouseModalProps) {
  const authHttp = useAuthHttp();
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    []
  );
  const [selectedWarehouse, setSelectedWarehouse] =
    useState<WarehouseOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen || !draft) return undefined;
    let active = true;
    const supplyChainProcessId = draft.id;
    setWarehouseOptions([]);
    setSelectedWarehouse(null);
    setError(undefined);

    const getFulfillableWarehouses = async () => {
      try {
        const { data } = await authHttp.post<FulfillableWarehousesResponse>(
          FULFILLABLE_WAREHOUSES_URL,
          { supplyChainProcessId }
        );
        if (active) {
          setWarehouseOptions(
            data.warehouses.map((warehouse) => ({
              label: warehouse.warehouseName,
              value: warehouse.warehouseId,
              disabled: !warehouse.canFulfil,
              hint: warehouse.canFulfil
                ? undefined
                : warehouse.reason ?? 'Not enough stock',
            }))
          );
        }
      } catch (fetchError) {
        if (active) {
          setError(
            'Could not load fulfilment options — close and reopen to retry.'
          );
        }
      }
    };

    getFulfillableWarehouses();
    return () => {
      active = false;
    };
  }, [isOpen, draft?.id]);

  if (!draft || !draft.salesOrder) return null;

  const order = draft.salesOrder;
  const totalItems = order.lines.reduce((sum, line) => sum + line.quantity, 0);
  const { deliveryAddress } = order;

  const handleConvert = async () => {
    if (!selectedWarehouse || isSubmitting) return;
    setIsSubmitting(true);
    setError(undefined);
    try {
      await authHttp.post(CONVERT_SALES_ORDER_URL, {
        supplyChainProcessId: draft.id,
        warehouseId: selectedWarehouse.value,
      });
      onActioned();
      setIsOpen(false);
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.errors?.[0]?.errorMessage ??
          submitError?.response?.data?.message ??
          'Could not create the despatch note. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIgnore = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(undefined);
    try {
      await authHttp.post(IGNORE_SALES_ORDER_URL, {
        supplyChainProcessId: draft.id,
      });
      onActioned();
      setIsOpen(false);
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.errors?.[0]?.errorMessage ??
          submitError?.response?.data?.message ??
          'Could not ignore the order. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={`Order ${order.orderNumber}`}
      className='w-full sm:w-[460px]'
    >
      <div className='flex flex-col gap-4'>
        <div className='rounded-lg bg-gray-50 px-4 py-3'>
          <div className='flex items-center justify-between'>
            <span className='text-sm font-medium text-gray-900'>
              {order.customerName}
            </span>
            <span className='text-sm text-gray-500'>
              {order.currency} {order.totalPrice.toFixed(2)}
            </span>
          </div>
          <p className='mt-1 text-xs text-gray-500'>
            {order.lines.length} line{order.lines.length === 1 ? '' : 's'} ·{' '}
            {totalItems} item{totalItems === 1 ? '' : 's'}
          </p>
          <ul className='mt-2 flex flex-col gap-1'>
            {order.lines.map((line) => (
              <li
                key={line.skuId}
                className='flex justify-between text-xs text-gray-600'
              >
                <span className='truncate pr-2'>{line.skuName}</span>
                <span className='shrink-0 text-gray-400'>×{line.quantity}</span>
              </li>
            ))}
          </ul>
        </div>

        {deliveryAddress && (
          <div className='rounded-lg bg-gray-50 px-4 py-3'>
            <p className='mb-2 text-[11px] font-medium uppercase tracking-wider text-gray-400'>
              Customer details
            </p>
            <dl className='grid grid-cols-2 gap-x-4 gap-y-2'>
              <CustomerDetail
                label='Address'
                value={joinAddressLines(deliveryAddress)}
              />
              <CustomerDetail
                label='Postcode'
                value={deliveryAddress.postcode ?? ''}
              />
              <CustomerDetail
                label='Phone'
                value={deliveryAddress.phone ?? ''}
              />
              <CustomerDetail
                label='Email'
                value={deliveryAddress.email ?? ''}
              />
            </dl>
          </div>
        )}

        <Select
          label='Fulfilment warehouse'
          placeholder='Select a warehouse'
          subText='Stock is picked and despatched from here.'
          options={warehouseOptions}
          selected={selectedWarehouse}
          setSelected={(option) =>
            setSelectedWarehouse(option as WarehouseOption)
          }
        />

        {warehouseOptions.length > 0 &&
          warehouseOptions.every((option) => option.disabled) && (
            <p className='text-sm text-amber-600'>
              No contracted warehouse holds enough stock for this order. Load
              stock for these SKUs, or ignore the order.
            </p>
          )}

        {error && <p className='text-sm text-red-600'>{error}</p>}

        <div className='mt-2 flex items-center justify-between gap-3'>
          <Button
            label='Ignore order'
            intent='tertiary'
            size='sm'
            onClick={handleIgnore}
            disabled={isSubmitting}
          />
          <div className='flex gap-3'>
            <Button
              label='Cancel'
              intent='secondary'
              size='sm'
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
            />
            <Button
              label={isSubmitting ? 'Creating…' : 'Create despatch note'}
              intent='primary'
              size='sm'
              onClick={handleConvert}
              disabled={!selectedWarehouse || isSubmitting}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
